import React, { useState, useEffect, useCallback } from 'react';
import { Volume2, Volume1, VolumeX } from 'lucide-react';
import { Slider } from '../components/ui/slider';

const STORAGE_KEYS = {
  video: 'playback_volume_video',
  audio: 'playback_volume_audio',
  voice_mix: 'voice_mix_level',
  music_mix: 'music_mix_level',
};

const DEFAULT_VOLUMES = {
  video: 0.8,
  audio: 0.8,
  voice_mix: 1.0,
  music_mix: 0.55,
};

function getStoredVolume(key) {
  try {
    const val = localStorage.getItem(STORAGE_KEYS[key]);
    return val !== null ? parseFloat(val) : DEFAULT_VOLUMES[key];
  } catch {
    return DEFAULT_VOLUMES[key];
  }
}

function storeVolume(key, value) {
  try { localStorage.setItem(STORAGE_KEYS[key], value.toString()); } catch {}
}

/**
 * VolumeControl — compact volume slider with mute toggle.
 * Props:
 *   mediaRef:  React.RefObject<HTMLMediaElement>
 *   type:      'video' | 'audio'
 *   className: optional
 *   compact:   show icon-only until hover (default false)
 */
export function VolumeControl({ mediaRef, type = 'audio', className = '', compact = false }) {
  const [volume, setVolume] = useState(() => getStoredVolume(type));
  const [muted, setMuted] = useState(false);
  const [expanded, setExpanded] = useState(!compact);

  const applyVolume = useCallback((v, m) => {
    if (mediaRef?.current) {
      mediaRef.current.volume = m ? 0 : v;
      mediaRef.current.muted = m;
    }
  }, [mediaRef]);

  useEffect(() => {
    applyVolume(volume, muted);
  }, [volume, muted, applyVolume]);

  const handleVolumeChange = (vals) => {
    const v = vals[0] / 100;
    setVolume(v);
    setMuted(v === 0);
    storeVolume(type, v);
    applyVolume(v, v === 0);
  };

  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    applyVolume(volume, next);
  };

  const VIcon = muted || volume === 0 ? VolumeX : volume < 0.5 ? Volume1 : Volume2;

  return (
    <div
      className={`flex items-center gap-2 ${className}`}
      onMouseEnter={() => compact && setExpanded(true)}
      onMouseLeave={() => compact && setExpanded(false)}
      data-testid={`volume-control-${type}`}
    >
      <button
        onClick={toggleMute}
        className="w-8 h-8 flex items-center justify-center rounded-lg bg-zinc-800/80 hover:bg-zinc-700 text-zinc-300 hover:text-white transition-colors shrink-0"
        aria-label={muted ? 'Unmute' : 'Mute'}
        data-testid={`mute-toggle-${type}`}
      >
        <VIcon className="w-4 h-4" />
      </button>
      {expanded && (
        <Slider
          value={[muted ? 0 : Math.round(volume * 100)]}
          onValueChange={handleVolumeChange}
          max={100}
          step={1}
          className="w-24"
          aria-label={`${type} volume`}
          data-testid={`volume-slider-${type}`}
        />
      )}
      {expanded && (
        <span className="text-[10px] text-zinc-500 w-8 text-right tabular-nums">
          {muted ? 0 : Math.round(volume * 100)}%
        </span>
      )}
    </div>
  );
}

/**
 * AudioMixPanel — collapsible voiceover + music mix sliders.
 * For video preview where dual audio tracks exist or mix prefs are stored.
 */
export function AudioMixPanel({ onMixChange, className = '' }) {
  const [open, setOpen] = useState(false);
  const [voiceVol, setVoiceVol] = useState(() => getStoredVolume('voice_mix'));
  const [musicVol, setMusicVol] = useState(() => getStoredVolume('music_mix'));

  const emitChange = useCallback((v, m) => {
    onMixChange?.({ voiceover: v, music: m });
  }, [onMixChange]);

  const handleVoice = (vals) => {
    const v = vals[0] / 100;
    setVoiceVol(v);
    storeVolume('voice_mix', v);
    emitChange(v, musicVol);
  };

  const handleMusic = (vals) => {
    const v = vals[0] / 100;
    setMusicVol(v);
    storeVolume('music_mix', v);
    emitChange(voiceVol, v);
  };

  const resetDefaults = () => {
    setVoiceVol(DEFAULT_VOLUMES.voice_mix);
    setMusicVol(DEFAULT_VOLUMES.music_mix);
    storeVolume('voice_mix', DEFAULT_VOLUMES.voice_mix);
    storeVolume('music_mix', DEFAULT_VOLUMES.music_mix);
    emitChange(DEFAULT_VOLUMES.voice_mix, DEFAULT_VOLUMES.music_mix);
  };

  return (
    <div className={`${className}`} data-testid="audio-mix-panel">
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-2 text-xs text-zinc-400 hover:text-white transition-colors w-full py-1.5"
        data-testid="audio-mix-toggle"
      >
        <svg className={`w-3 h-3 transition-transform ${open ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
        Audio Mix
      </button>

      {open && (
        <div className="mt-2 p-3 bg-zinc-800/60 rounded-xl space-y-3 animate-in fade-in slide-in-from-top-1 duration-200">
          {/* Voiceover slider */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs text-zinc-400 flex items-center gap-1.5" htmlFor="voice-slider">
                <span>Voiceover</span>
              </label>
              <span className="text-[10px] text-zinc-500 tabular-nums">{Math.round(voiceVol * 100)}%</span>
            </div>
            <Slider
              id="voice-slider"
              value={[Math.round(voiceVol * 100)]}
              onValueChange={handleVoice}
              max={100}
              step={1}
              aria-label="Voiceover volume"
              data-testid="voice-mix-slider"
            />
          </div>

          {/* Music slider */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-xs text-zinc-400 flex items-center gap-1.5" htmlFor="music-slider">
                <span>Music</span>
              </label>
              <span className="text-[10px] text-zinc-500 tabular-nums">{Math.round(musicVol * 100)}%</span>
            </div>
            <Slider
              id="music-slider"
              value={[Math.round(musicVol * 100)]}
              onValueChange={handleMusic}
              max={100}
              step={1}
              aria-label="Music volume"
              data-testid="music-mix-slider"
            />
          </div>

          {/* Reset button */}
          <button
            onClick={resetDefaults}
            className="text-[10px] text-zinc-500 hover:text-primary transition-colors"
            data-testid="mix-reset-btn"
          >
            Reset to default
          </button>
        </div>
      )}
    </div>
  );
}

// Re-export utility for studios
export { getStoredVolume, storeVolume, STORAGE_KEYS, DEFAULT_VOLUMES };
